import { atom } from 'jotai'
import { atomFamily, selectAtom } from 'jotai/utils'

export interface InvokeErrorEntry {
  errorMessage: string
  errorCode: string
  errorAt: number
}

export interface UndoErrorEntry {
  errorMessage: string
}

export const invokeErrorsAtom = atom(new Map<string, InvokeErrorEntry>())
export const undoErrorsAtom = atom(new Map<string, UndoErrorEntry>())

export const pendingDisplayAtom = atom(new Map<string, number>())
/**
 * Archive counterpart of `pendingDisplayAtom`: ids whose archive just ran from
 * this window (or a peer), keeping the row visible for the undo window before
 * the archived filter takes it.
 */
export const archivedDisplayAtom = atom(new Map<string, number>())
export const manuallyHiddenAtom = atom(new Set<string>())

export const invokingIdAtom = atom<string | null>(null)
export const undoingIdAtom = atom<string | null>(null)

/**
 * Per-id selectors over the shared maps above. Each `ViewActionCell` subscribes
 * only to its own action's slice via these families, so a status change for one
 * action no longer re-renders (or re-sorts) every other mounted cell. `selectAtom`
 * keeps the derived value referentially stable until that id's entry changes.
 */
export const invokeErrorByIdAtom = atomFamily((id: string) => selectAtom(invokeErrorsAtom, (map) => map.get(id)))
export const undoErrorByIdAtom = atomFamily((id: string) => selectAtom(undoErrorsAtom, (map) => map.get(id)))
export const pendingStartByIdAtom = atomFamily((id: string) => selectAtom(pendingDisplayAtom, (map) => map.get(id)))
export const archivedStartByIdAtom = atomFamily((id: string) => selectAtom(archivedDisplayAtom, (map) => map.get(id)))
export const isInvokingByIdAtom = atomFamily((id: string) => selectAtom(invokingIdAtom, (current) => current === id))
export const isUndoingByIdAtom = atomFamily((id: string) => selectAtom(undoingIdAtom, (current) => current === id))

// atomFamily caches never evict by default, so a long-lived overlay session
// would accrue one entry per action id ever rendered. Lazy TTL eviction keeps
// the cache bounded; recreating an evicted entry is safe and cheap because the
// selectAtoms are pure derivations over the source atoms (no state of their own).
const PER_ID_ATOM_TTL_MS = 60 * 60 * 1000
const shouldRemoveStale = (createdAt: number) => Date.now() - createdAt > PER_ID_ATOM_TTL_MS
invokeErrorByIdAtom.setShouldRemove(shouldRemoveStale)
undoErrorByIdAtom.setShouldRemove(shouldRemoveStale)
pendingStartByIdAtom.setShouldRemove(shouldRemoveStale)
archivedStartByIdAtom.setShouldRemove(shouldRemoveStale)
isInvokingByIdAtom.setShouldRemove(shouldRemoveStale)
isUndoingByIdAtom.setShouldRemove(shouldRemoveStale)
